import { Server } from 'lucide-react'
import { useCallback, useRef } from 'react'
import { HealthPanel } from '@/components/engine/health-panel'
import { BYTES_PER_MB, MemoryChart, type MemoryDataPoint } from '@/components/engine/memory-chart'
import { MetricsGrid } from '@/components/engine/metrics-grid'
import { QueuePanel } from '@/components/engine/queue-panel'
import { ThroughputChart, type ThroughputDataPoint } from '@/components/engine/throughput-chart'
import { useEngineHealth, useEngineMetrics, useFlushQueue, useQueueStats } from '@/hooks/use-engine'
import type { EngineMetrics } from '@/lib/types'

const MAX_HISTORY_POINTS = 20
const METRICS_INTERVAL_SECONDS = 15

export function EnginePage(): React.JSX.Element {
  const healthQuery = useEngineHealth()
  const queueQuery = useQueueStats()
  const metricsQuery = useEngineMetrics()
  const flushMutation = useFlushQueue()

  const previousMetricsRef = useRef<EngineMetrics | undefined>(undefined)
  const throughputHistoryRef = useRef<ThroughputDataPoint[]>([])
  const memoryHistoryRef = useRef<MemoryDataPoint[]>([])
  const lastUpdatedRef = useRef<Date | null>(null)

  // Track when metrics data changes to accumulate history
  const currentMetrics = metricsQuery.data
  const prevForDisplay = previousMetricsRef.current

  // Update history whenever we get new metrics data
  const updateHistory = useCallback(() => {
    if (!currentMetrics) return
    const prev = previousMetricsRef.current
    const now = new Date()
    const timeLabel = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`

    // Calculate rates if we have a previous snapshot
    if (prev) {
      const accepted =
        Math.max(0, currentMetrics.messages_accepted - prev.messages_accepted) /
        METRICS_INTERVAL_SECONDS
      const delivered =
        Math.max(0, currentMetrics.messages_delivered - prev.messages_delivered) /
        METRICS_INTERVAL_SECONDS
      const bounced =
        Math.max(0, currentMetrics.messages_bounced - prev.messages_bounced) /
        METRICS_INTERVAL_SECONDS
      const deferred =
        Math.max(0, currentMetrics.messages_deferred - prev.messages_deferred) /
        METRICS_INTERVAL_SECONDS

      const point: ThroughputDataPoint = {
        time: timeLabel,
        accepted: Math.round(accepted * 10) / 10,
        delivered: Math.round(delivered * 10) / 10,
        bounced: Math.round(bounced * 10) / 10,
        deferred: Math.round(deferred * 10) / 10,
      }

      throughputHistoryRef.current = [
        ...throughputHistoryRef.current.slice(-MAX_HISTORY_POINTS + 1),
        point,
      ]
    }

    // Always track memory
    const memPoint: MemoryDataPoint = {
      time: timeLabel,
      memory_mb: Math.round((currentMetrics.memory_bytes / BYTES_PER_MB) * 10) / 10,
    }
    memoryHistoryRef.current = [
      ...memoryHistoryRef.current.slice(-MAX_HISTORY_POINTS + 1),
      memPoint,
    ]

    previousMetricsRef.current = currentMetrics
    lastUpdatedRef.current = now
  }, [currentMetrics])

  // We use a ref to track the last dataUpdatedAt so we only process each new fetch once
  const lastProcessedAtRef = useRef<number>(0)
  if (metricsQuery.dataUpdatedAt > lastProcessedAtRef.current && currentMetrics) {
    lastProcessedAtRef.current = metricsQuery.dataUpdatedAt
    updateHistory()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Server className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">SMTP Engine</h1>
      </div>

      {/* Health panel */}
      <HealthPanel health={healthQuery.data} isLoading={healthQuery.isLoading} />

      {/* Metrics grid */}
      <MetricsGrid
        metrics={currentMetrics}
        previousMetrics={prevForDisplay}
        intervalSeconds={METRICS_INTERVAL_SECONDS}
        isLoading={metricsQuery.isLoading}
        lastUpdated={lastUpdatedRef.current}
      />

      {/* Charts row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ThroughputChart data={throughputHistoryRef.current} />
        <MemoryChart data={memoryHistoryRef.current} />
      </div>

      {/* Queue panel */}
      <QueuePanel
        stats={queueQuery.data}
        isLoading={queueQuery.isLoading}
        onFlush={() => flushMutation.mutate()}
        isFlushing={flushMutation.isPending}
      />
    </div>
  )
}
